import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { AgentLogsQueryParams } from '../mapAgentLogsFiltersToQueryParams';

import { agentLogsQueryKeys, listAgentExecutions } from '~/api/agentLogs';
import { MONITOR_PAGE_SIZE } from '~/api/monitorService';

type UseAgentLogsDataParams = {
  realm: string;
  environment: string;
  queryParams: AgentLogsQueryParams;
  enabled?: boolean;
};

export function useAgentLogsData({
  realm,
  environment,
  queryParams,
  enabled = true
}: UseAgentLogsDataParams) {
  const { filters, dateFrom, dateTo } = queryParams;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    error,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: agentLogsQueryKeys.list({
      realm,
      environment,
      filters,
      dateFrom,
      dateTo,
      limit: MONITOR_PAGE_SIZE,
      order: 'descending'
    }),
    queryFn: ({ pageParam }) =>
      listAgentExecutions({
        realm,
        environment,
        filters,
        dateFrom,
        dateTo,
        limit: MONITOR_PAGE_SIZE,
        order: 'descending',
        cursor: pageParam
      }),
    getNextPageParam: lastPage =>
      lastPage.pagination?.hasNext ? lastPage.pagination.nextCursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && !!realm && !!environment,
    staleTime: 0,
    refetchOnWindowFocus: false
  });

  const items = useMemo(
    () => data?.pages.flatMap(page => page.items) ?? [],
    [data]
  );

  const logTotal = data?.pages[0]?.logTotal ?? 0;

  return {
    items,
    logTotal,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    error: error as Error | null,
    hasMore: hasNextPage ?? false,
    handleLoadMore: fetchNextPage
  };
}
